<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { CouponUpdateDocument, CouponType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { formatDate, formatDateStringToIso } from '@/utils/date-utils';
import { capitalizeFirstLetter } from '@/utils/string-utils';

// --- Component properties ---
const props = defineProps<{
  coupon: CouponType;
  show: boolean;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});
const newUnitsRemaining = ref<number | null>(
  props.coupon.unitsRemaining || null
);
const newExpiresAt = ref<Date | null>(props.coupon.expiresAt);
const clearExpirationDate = ref<boolean>(props.coupon.expiresAt !== null);

// --- Mutation ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(CouponUpdateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.couponUpdate) {
    console.debug('Coupon updated:', res.data.couponUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Coupon update failed: ' + res.data;
    console.error('Coupon update failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error updating coupon:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function close() {
  emits('close');
}

function updateRequested() {
  console.debug('Update requested');

  if (!props.coupon.id) {
    mutationError.value = 'Coupon ID is missing';
    console.error('Coupon ID is missing');
    return;
  }

  let expirationDate: string | null = null;

  if (!clearExpirationDate.value) {
    expirationDate = formatDateStringToIso(newExpiresAt.value);
  }

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    id: props.coupon.id,
    unitsRemaining: newUnitsRemaining.value,
    expiresAt: expirationDate,
    clearExpirationDate: clearExpirationDate.value,
  });
}
</script>
<template>
  <div>
    <BModal v-model="show" @hide="close">
      <!-- Modal Header -->
      <template #title>
        <font-awesome-icon icon="ticket" class="me-2" />
        {{ capitalizeFirstLetter(coupon.couponType) }} coupon
      </template>

      <!-- Modal Body-->
      <div>
        <p>
          <strong>Current status: </strong>
          {{ coupon.unitsRemaining }} of
          {{ coupon.unitsTotal }}
          {{ coupon.units }} remaining<br />
          <strong>Expires: </strong>
          {{ formatDate(coupon.expiresAt, 'no expiration') }}
        </p>

        <!-- Units Remaining -->
        <BFormGroup
          :label="`${capitalizeFirstLetter(coupon.units)} remaining`"
          label-for="new-units-remaining"
          class="mt-3"
        >
          <BFormInput
            v-model.number="newUnitsRemaining"
            id="new-units-remaining"
            name="new-units-remaining"
            type="number"
            placeholder="Enter the number of units remaining"
            min="0"
          ></BFormInput>
        </BFormGroup>

        <!-- Expiration Date -->
        <BFormGroup
          label="Expiration date"
          label-for="new-expiration-date"
          class="mt-3"
        >
          <BFormInput
            v-model="newExpiresAt"
            id="new-expiration-date"
            name="new-expiration-date"
            placeholder="Enter a new expiration date"
            type="date"
            :disabled="clearExpirationDate"
          ></BFormInput>
        </BFormGroup>

        <!-- Clear Expiration Date Checkbox -->
        <BFormGroup class="mt-3">
          <BFormCheckbox
            v-model="clearExpirationDate"
            id="clear-expiration-date"
            name="clear-expiration-date"
          >
            Clear expiration date
          </BFormCheckbox>
        </BFormGroup>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <BButton @click="close" variant="secondary"> Close </BButton>
        <BButton
          @click="updateRequested"
          :disabled="mutationInProgress || mutationShowSuccess"
          variant="primary"
        >
          <span v-if="mutationInProgress">
            Updating Coupon
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Coupon Updated
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Update Coupon </span>
        </BButton>
      </template>
    </BModal>
  </div>
</template>
